import {
    useAddTeamMemberMutation, useFetchInvitedUserQuery, useReinviteInviteMemberMutation,
    useRemoveInvitedMemberMutation,
    useRemoveTeamMemberMutation, useUpdateProjectAccessLevelMutation,
} from 'api/collaborateApi.ts';
import { useFetchProjectContributorsQuery } from 'api/contributorsApi.ts';
import { useGetPagesQuery } from 'api/siteApi.ts';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'sonner';
import { accessOptions, reshareLink, userRoles } from 'ui/components/helper.ts';
import { Option } from 'ui/components/MultiSelect';
import ResponsiveDialog from 'ui/components/ResponsiveDialog';
import ShareProjectModal, { UserOption } from 'ui/components/ShareModal';
import { AccessObject } from 'ui/components/ShareModal/access-option.tsx';
import { PeopleProps } from 'ui/components/ShareModal/access-people-table.tsx';
import { planvalue } from 'utils/constants.ts';
import { normalizeUrl } from 'utils/proxyUtils.ts';
import useGetPageId from '../../../hooks/use-get-pageid.tsx';
import { useLockFeature } from '../../../hooks/use-lock-feature.tsx';
import { getSelectedSiteCollaborateInfo } from '../../../store/siteInfoSlice.ts';
import { getShareModalOpen, setShareModalOpen } from "../../../store/uiSlice";
import { getUser, setShowLockFeature } from '../../../store/userSlice.ts';
import "./share-modal.css";

interface Props {
    siteId: string
    siteRole: string
    workspaceId: number
}

const lockTitle = "Project Permissions"
const lockDescription = "	Add screenshots, documents, or assets to any task — everything your team needs, right where the feedback happens."
const lockModalImgSetting = 'https://wpfeedback-image.s3.us-east-2.amazonaws.com/images/integration-lock-images/lock-user-image-3.jpg'


export const ShareModal = ({ siteId, siteRole, workspaceId }: Props) => {
    const open = useSelector(getShareModalOpen)
    const dispatch = useDispatch()
    const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo)
    const userInfo = useSelector(getUser)
    const { pageId } = useGetPageId();
    const { data } = useGetPagesQuery({ siteId });

    const ActivePageUrl = useMemo(() => {

        // Early return if no pages data exists
        const pages = data?.data?.pages?.page;
        const fallbackUrl = collaborateInfo?.result?.url_host_path;

        // If we have pages data and a pageId, try to find the matching page
        if ((pages && pages?.length > 0) && pageId) {
            const currentPage = pages.find((page) => page.id === pageId);
            const url = !window.ATARIM
                ? currentPage?.target_url
                : currentPage?.url;
            if (!url) {
                console.warn('No URL found for the current page:', currentPage);
                return fallbackUrl;
            }
            const normalizedUrl = normalizeUrl(url, siteId);
            return normalizedUrl || fallbackUrl;
        }

        // Return fallback URL if no pages or no matching page
        return fallbackUrl;

    }, [data?.data?.pages?.page, pageId, collaborateInfo?.result?.url_host_path, siteId]);


    // Links
    const inviteTeamLink = `${import.meta.env.VITE_APP_ATARIM_URL || process.env.CEB_VITE_APP_ATARIM_URL}/register?admin_id=${userInfo.adminId}&workspace_id=${workspaceId}&project_id=${siteId}`;
    const inviteLinkCode = collaborateInfo?.result.link_code

    const basePreviewUrl = import.meta.env.VITE_APP_SHARE_PREVIEW || process.env.CEB_VITE_APP_SHARE_PREVIEW;
    const linkSuffix = inviteLinkCode ? `/${inviteLinkCode}?collab=true${pageId ? `&p=${pageId}` : ""}` : `?item=${siteId}&collab=true${pageId ? `&p=${pageId}` : ""}`;
    // const collabLink = !isInIframe() || collaborateInfo?.result.type === 'site'
    //     ? ActivePageUrl : `${basePreviewUrl}${linkSuffix}`;
    // TODO: To be decided if we want to use the ActivePageUrl or the basePreviewUrl
    // For now, we will use the basePreviewUrl
    const collabLink = `${basePreviewUrl}${linkSuffix}`;
    const [accessLevel, setAccessLevel] = useState<AccessObject>({} as AccessObject)
    const [guestMembersToAdd, setGuestMembersToAdd] = useState<Option[]>([])
    const [guestNote, setGuestNote] = useState("")
    const [role, setRole] = useState<Option>(userRoles[1])
    const [shareNote, setShareNote] = useState("")
    const [teamMembersToAdd, setTeamMembersToAdd] = useState<Option[]>([])
    const [newEmails, setNewEmails] = useState<Option[]>([]);
    const planRank = userInfo?.raw?.plan?.rank === 4 ? 2 : userInfo?.raw?.plan?.rank ?? 2;
    const {
        activePlan,
        calculateBasePrice,
        calculateBaseTeamSeatCost,
        returnPlanPeriodValue,
        handleSubscribe,
        // handleComparePlans
    } = useLockFeature({ rank: planRank || 2 });

    const userCount = newEmails.length - (userInfo?.raw?.limit?.users || 0);
    const teamPrice = activePlan && Number(calculateBaseTeamSeatCost()) * Number(userCount);
    const basePrice = activePlan && calculateBasePrice(activePlan)
    const additionalSeatPrice = activePlan && calculateBaseTeamSeatCost()
    const currentInterval = activePlan && returnPlanPeriodValue(
        'month',
        'year',
        '2years'
    )
    const userRank = userInfo?.raw?.plan?.rank || 0
    // const plan = userInfo?.raw && planvalue[userRank]
    const plan = userInfo?.raw && planvalue[userRank as keyof typeof planvalue]

    // APIs
    const { data: contributors, isLoading: isLoadingContributors } = useFetchProjectContributorsQuery({
        site_id: siteId,
        workspace_id: workspaceId,
    })
    const { data: invitedUsers } = useFetchInvitedUserQuery(siteId)
    const [inviteTeamMember, { isLoading: isInvitingMember }] = useAddTeamMemberMutation()
    const [removeTeamMember] = useRemoveTeamMemberMutation()
    const [removeInvitedMember] = useRemoveInvitedMemberMutation()
    const [reinviteMember] = useReinviteInviteMemberMutation()
    const [updateAccessLevel] = useUpdateProjectAccessLevelMutation()

    // Data mapping
    useEffect(() => {
        if (!collaborateInfo) return
        setAccessLevel(accessOptions.find(o => o.is_public === collaborateInfo.result.is_public) as AccessObject)
    }, [collaborateInfo]);
    const peopleWithAccess: PeopleProps[] = useMemo(() => {
        if (!contributors && !invitedUsers) return [];
        const currentContributor = contributors?.data?.filter(item => item.selected).map((contributor) => ({
            id: contributor.id.toString(),
            name: contributor.name,
            email: contributor.email,
            src: contributor.avatar ?? "",
            selected: contributor.selected,
            role: contributor?.role === 'contributor' ? "Team Member" : contributor.role,
            copyPeopleLink: collabLink || '',
            isOwner: contributor.is_owner ?? false,
        }));

        const pendingInvitedUsers = invitedUsers
            ? invitedUsers.map((pending) => ({
                id: pending.id.toString(),
                name: pending.collaborator.name,
                email: pending.collaborator.email,
                src: "",
                selected: false,
                role: 'Pending',
                copyPeopleLink: reshareLink(siteId, pending.collaborator.token, pending.collaborator.role),
                isOwner: false
            }))
            : []; // Fallback to an empty array if invitedUsers is undefined
        return [...(currentContributor || []), ...pendingInvitedUsers];
    }, [collabLink, contributors, invitedUsers, siteId])

    const teamMembers: UserOption[] = useMemo(() => {
        if (!contributors) return [];
        return contributors?.data?.map((contributor) => ({
            value: contributor.email || '',
            label: contributor.name || '',
            email: contributor.email || '',
            src: contributor.avatar ?? undefined,
            selected: contributor.selected,
            role: contributor?.role === 'contributor' ? "Team Member" : contributor?.role,
        }));
    }, [contributors])

    // Handlers
    const handleOpenChange = (open: boolean) => {
        dispatch(setShareModalOpen(open))
    }
    const locked = !userInfo?.raw?.plan?.private_sites_enabled;
    const handleAccessLevelChange = (level: AccessObject) => {
        if (locked) {
            dispatch(setShowLockFeature({
                showLockFeature: true,
                lockTitle: lockTitle,
                lockParagraph: lockDescription,
                lockImg: lockModalImgSetting,
                rank: 3,
            }));
            return;
        }
        setAccessLevel(level)
        updateAccessLevel({ params: { is_public: level.is_public, site_id: siteId } })
    }
    const handleDelete = (id: string | number, role: string) => {
        if (role === "Pending") {
            removeInvitedMember({ site_id: siteId, params: { token_id: id.toString() } })
        } else {
            removeTeamMember({ site_id: siteId, params: { contributor_id: id.toString(), attach_contributor: false } })
        }
    }
    const handleInviteGuest = () => {
        inviteTeamMember({
            site_id: siteId,
            params: {
                emails: guestMembersToAdd.map(g => g.value),
                role: "collaborator",
                ...(shareNote && { message: guestNote }),
                ...(pageId && { page_id: pageId }),
            }
        }).unwrap().then(() => {
            toast.success("Invite successfully sent!")
        }
        ).catch(err => {
            console.error(`Error inviting guest`, err)
            toast.error("Error inviting guest!")
        })
        setGuestMembersToAdd([])
        setGuestNote("")
    }
    const handleGuestMembersChange = (members: Option[]) => {
        setGuestMembersToAdd(members)
    }
    const handleGuestNoteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setGuestNote(e.target.value)
    }
    const handleReinvite = (id: string | number) => {
        reinviteMember({ site_id: siteId, params: { invite_id: id.toString(), site_id: siteId } })
    }
    const handleRoleChange = (role: Option) => {
        setRole(role)
    }
    const handleShareNoteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setShareNote(e.target.value)
    }

    const inviteMoreStatus = newEmails.length > (userInfo?.raw?.limit?.users || 0);

    const handleSubmitTeamMembers = () => {
        const userlimit = userInfo?.raw?.limit?.users || 0
        if (newEmails.length > userlimit) {
            const user = Number(userCount) + Number(userInfo.raw?.plan?.users)
            handleSubscribe({ user })
            console.error(`You are trying to invite ${newEmails.length} team member(s), but you need more seats! Unlock more and start collaborating with your team and get shit done!`);
            return;
        }
        inviteTeamMember({
            site_id: siteId,
            params: {
                emails: teamMembersToAdd.map(user => user.value),
                role: role.value,
                ...(shareNote && { message: shareNote }),
                ...(pageId && { page_id: pageId }),
            },
        }).unwrap().then(() => {
            toast.success("Invite successfully sent!")
        }).catch(err => {
            console.error(`Error inviting member`, err)
            toast.error("Error inviting member!")
        })
        setTeamMembersToAdd([])
        setRole(userRoles[1])
    }
    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const handleTeamMembersChange = (members: UserOption[]) => {
        // Find emails in `users` not present in `teamMembers`
        const emailsNotInFirst = members.filter(
            (user) => !teamMembers.some((member) => member.value === user.value)
        );

        // Show warning if unauthorized users are found
        setNewEmails(emailsNotInFirst)
        if (collaborateInfo && collaborateInfo.result?.role === "contributor") {
            if (emailsNotInFirst.length > 0) {
                toast.warning(
                    `You do not have permission on this project to invite new team members: ${emailsNotInFirst
                        .map((email) => email.label)
                        .join(", ")}`
                );
                return; // Prevent further processing
            }
        }

        // Map `users` to include additional properties
        const newTeamMembers = members.map((user) => ({
            value: user.value,
            label: user.label,
            isContributor: collaborateInfo && collaborateInfo.result?.role === "contributor" && !teamMembers.some((member) => member.value === user.value) || false,
            isvalidEmail: isValidEmail(user.value)
        }));

        // Find users with invalid email addresses
        const invalidEmails = newTeamMembers.filter((user) => !user.isvalidEmail);
        if (invalidEmails.length > 0) {
            console.error(`Please enter valid email address for: ${invalidEmails
                .map((user) => user.label)
                .join(", ")}`)
            toast.error(
                `Please enter valid email address for: ${invalidEmails
                    .map((user) => user.label)
                    .join(", ")}`
            );
            return;
        }

        setTeamMembersToAdd(newTeamMembers)
    }

    return <ResponsiveDialog onOpenChange={handleOpenChange} open={open}>
        <ShareProjectModal
            container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper') || document.body}
            // Contacts
            users={teamMembers}
            // Change of people in add team members
            handleMultiSelectChange={handleTeamMembersChange}
            // People in add team members
            multiSelectValue={teamMembersToAdd}
            // Team note
            handleChangeShareNote={handleShareNoteChange}
            shareNote={shareNote}
            // All user roles possible
            userRoles={userRoles}
            // User role
            roleValue={role}
            handleSelectRole={handleRoleChange}
            // Guests to add
            handleGuestMultiSelectOnChange={handleGuestMembersChange}
            multiSelectguestValue={guestMembersToAdd}
            // Guest note
            handleChangeGuestNote={handleGuestNoteChange}
            guestNote={guestNote}
            // Submit
            handleSubmitTeamMembers={handleSubmitTeamMembers}
            handleInviteGuest={handleInviteGuest}
            // Access level - admin screen
            accessOptions={accessOptions}
            handleAccessLevelChange={handleAccessLevelChange}
            accessLevel={accessLevel}
            // People - also admin screen
            people={peopleWithAccess}
            onDelete={handleDelete}
            // Resend
            onResendInvite={handleReinvite}
            // Links for copying
            inviteTeamLink={inviteTeamLink}
            collabLink={collabLink || ''}
            // Loading states
            isInvitingMember={isInvitingMember}
            isLoadingContributors={isLoadingContributors}
            // Control access to things - admin / collaborator / guest
            websiteUserRole={siteRole}
            inviteMoreStatus={inviteMoreStatus}
            userCount={userCount}
            teamPrice={teamPrice}
            //   startingCheckout={startingCheckout}
            basePrice={(basePrice)}
            additionalSeatPrice={additionalSeatPrice}
            currentInterval={currentInterval}
            plan={plan}
        />
    </ResponsiveDialog>
}